import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

interface Clause {
  uuid: string;
  standardCode: string;
  versionYear: number;
  publicationStatus: string;
  clauseReference: string;
  clauseTitle: string;
  paraphrasedSummary: string;
  guidanceTipsUk: string | null;
  sortOrder: number;
}

interface ClausesResponse {
  standardCode: string;
  versionYear: number;
  clauses: Clause[];
}

export function StandardClauses() {
  const { slug, code, year } = useParams<{ slug: string; code: string; year: string }>();
  const [data, setData] = useState<ClausesResponse | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api<ClausesResponse>(`/standards/${code}/${year}/clauses`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [code, year]);

  const label = code && year ? `${code.replace(/^ISO/, "ISO ")}:${year}` : "Standard";

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/standards`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← All standards
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{label}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Clauses for this standard. Click a clause to read the paraphrased summary and Coach
          guidance, and to attach evidence.
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {data === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <ol className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800">
          {data.clauses.map((c) => (
            <li key={c.uuid}>
              <Link
                to={`/o/${slug}/standards/${data.standardCode}/${data.versionYear}/${encodeURIComponent(c.clauseReference)}`}
                className="flex items-start gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40"
              >
                <span className="mt-0.5 inline-flex min-w-[3rem] justify-center rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {c.clauseReference}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {c.clauseTitle}
                  </p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">
                    {c.paraphrasedSummary}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
